import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase/index.tsx';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconUsers } from '@sorare/core/src/atoms/icons/system/iconUsers.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { LabelS } from '@sorare/core/src/atoms/typography/index.tsx';
import { Chip } from '@sorare/core/src/atoms/ui/Chip/index.tsx';
import { ANY_SPORT_SERIES_SQUAD_MEMBERS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import type { SquadMembersButton_squad } from './__generated__/index.graphql.ts';
import squadListIcon from './assets/squadListIcon.webp';

const StyledButtonBase = styled(ButtonBase)`
  position: relative;
  isolation: isolate;
  width: calc(8 * var(--unit));
  height: calc(8 * var(--unit));
  display: flex;
`;
const MembersChipWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 50%);
  z-index: 1;
`;
const Img = styled.img`
  width: calc(8 * var(--unit));
  height: calc(8 * var(--unit));
  object-fit: contain;
`;

type Props = {
  squad: SquadMembersButton_squad;
};
export const SquadMembersButton = ({ squad }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { formatMessage } = useIntl();

  return (
    <StyledButtonBase
      to={generateSportContextPath(ANY_SPORT_SERIES_SQUAD_MEMBERS)}
    >
      <Img
        src={squadListIcon}
        alt={formatMessage({
          defaultMessage: 'Squad members',
          description:
            'Alt text for the button that redirects to the squad members page',
        })}
      />

      <MembersChipWrapper>
        <Chip color="transparent">
          <Horizontal gap={0.5}>
            <FontAwesomeIcon icon={iconUsers} />
            <LabelS bold>{squad.membersCount}</LabelS>
          </Horizontal>
        </Chip>
      </MembersChipWrapper>
    </StyledButtonBase>
  );
};

SquadMembersButton.fragments = {
  squad: gql`
    fragment SquadMembersButton_squad on Squad {
      id
      membersCount
    }
  ` as TypedDocumentNode<SquadMembersButton_squad>,
};
